/**
 * 旅のポスター書き出し
 *
 * Canvas 2D で 1 枚の縦長ポスターを組み立てて PNG として保存する。
 * レイアウトは 3 種類（ヒーロー / アートワーク / インフォグラフィック）から選べ、
 * コピーと写真選定は Gemini に旅の記録と実際の写真を見せて決めさせる。
 * 画像そのものの生成は行わない（Gemini API に使える画像生成モデルが無いため）。
 */

import { Download, Loader2, Sparkles } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'

import { generateTripPoster } from '@/api/services/trips'
import type { TripDay, TripDetail, TripPosterCopy, TripRoutePoint } from '@/api/types/trip'
import { Button } from '@/components/ui/button'
import { useSubmitLock } from '@/hooks/useSubmitLock'
import { shortPrefectureName } from '@/lib/prefectures'
import { cn } from '@/lib/utils'

import { renderArtworkPoster } from './poster/layout-artwork'
import { renderHeroPoster } from './poster/layout-hero'
import { renderInfographicPoster } from './poster/layout-infographic'
import type { PosterInput } from './poster/poster-canvas'
import { DEFAULT_ACCENT, HEIGHT, loadAssetBitmap, WIDTH } from './poster/poster-canvas'
import { dateRangeLabel } from './trip-utils'

type PosterVariant = 'hero' | 'artwork' | 'infographic'

const VARIANTS: { value: PosterVariant; label: string; hint: string }[] = [
  { value: 'hero', label: 'ヒーロー', hint: 'AI が選んだ 1 枚を全面に敷いた映画ポスター風' },
  { value: 'artwork', label: 'アートワーク', hint: '主役の写真をアクセント色の 2 色刷りに加工して文字を重ねる' },
  { value: 'infographic', label: '記録', hint: '1 日ごとの滞在帯・ルート・写真で振り返る' },
]

/** フィルムストリップに載せる写真の枚数 */
const FILMSTRIP_COUNT = 6

/** AI 仕上げの一時停止フラグ。画像生成 AI が使えるようになったら true に戻す */
const AI_ENABLED = false

type Props = {
  trip: TripDetail
  routePoints: TripRoutePoint[]
  days: TripDay[]
  photoAssetIds: string[]
  className?: string
}

/** 時系列に均等な間隔で写真を間引く */
const sample = (assetIds: string[], count: number) => {
  if (assetIds.length <= count) return assetIds
  const step = assetIds.length / count
  return Array.from({ length: count }, (_, i) => assetIds.at(Math.floor(i * step)) ?? '').filter(Boolean)
}

/** AI 未生成でもポスターが成立するようにテキストを埋める */
const fallbackCopy = (trip: TripDetail, days: TripDay[]) => ({
  accent: DEFAULT_ACCENT,
  headline: trip.title,
  subcopy: dateRangeLabel(trip),
  keywords: trip.stats.prefectures.slice(0, 3).map(shortPrefectureName),
  dayLines: days.map((day, i) => `${i + 1}日目 — ${day.visits.at(0)?.place?.display_name ?? '移動'}`),
})

export const TripPoster = ({ trip, routePoints, days, photoAssetIds, className }: Props) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [variant, setVariant] = useState<PosterVariant>('hero')
  const [copy, setCopy] = useState<TripPosterCopy | null>(null)
  const [isRendering, setIsRendering] = useState(true)
  const { isSubmitting: isGenerating, run: runGenerate } = useSubmitLock()

  const fallback = useMemo(() => fallbackCopy(trip, days), [trip, days])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    let cancelled = false
    setIsRendering(true)

    const run = async () => {
      const heroId = copy?.hero_asset_id ?? photoAssetIds.at(0) ?? null
      const collageIds = copy?.collage_asset_ids.length ? copy.collage_asset_ids : photoAssetIds.slice(1, 4)

      const [hero, collage, filmstrip] = await Promise.all([
        // アートワーク面はこの 1 枚を 2 色刷りに加工して背景にする
        variant !== 'infographic' && heroId ? loadAssetBitmap(heroId) : Promise.resolve(null),
        variant === 'hero'
          ? Promise.all(collageIds.map((id) => loadAssetBitmap(id, 'thumbnail')))
          : Promise.resolve([]),
        variant === 'infographic'
          ? Promise.all(sample(photoAssetIds, FILMSTRIP_COUNT).map((id) => loadAssetBitmap(id, 'thumbnail')))
          : Promise.resolve([]),
      ])
      if (cancelled) return

      const ctx = canvas.getContext('2d')
      if (!ctx) return
      canvas.width = WIDTH
      canvas.height = HEIGHT

      const input: PosterInput = {
        trip,
        routePoints,
        days,
        accent: copy?.accent_color ?? fallback.accent,
        headline: copy?.headline ?? fallback.headline,
        subcopy: copy?.subcopy ?? fallback.subcopy,
        keywords: copy?.keywords ?? fallback.keywords,
        dayLines: copy?.day_lines.length ? copy.day_lines : fallback.dayLines,
        hero,
        collage: collage.filter((b): b is ImageBitmap => b !== null),
        filmstrip: filmstrip.filter((b): b is ImageBitmap => b !== null),
      }

      if (variant === 'hero') renderHeroPoster(ctx, input)
      else if (variant === 'artwork') renderArtworkPoster(ctx, input)
      else renderInfographicPoster(ctx, input)
    }

    void run().finally(() => {
      if (!cancelled) setIsRendering(false)
    })

    return () => {
      cancelled = true
    }
  }, [variant, copy, trip, routePoints, days, photoAssetIds, fallback])

  const handleGenerate = useCallback(async () => {
    await runGenerate(async () => {
      try {
        setCopy(await generateTripPoster(trip.id))
      } catch {
        toast.error('AI ポスターの生成に失敗しました')
      }
    })
  }, [trip.id, runGenerate])

  const handleDownload = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    canvas.toBlob((blob) => {
      if (!blob) {
        toast.error('画像の書き出しに失敗しました')
        return
      }
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${trip.title}-${variant}.png`
      link.click()
      URL.revokeObjectURL(url)
    }, 'image/png')
  }, [trip.title, variant])

  const activeHint = VARIANTS.find((item) => item.value === variant)?.hint ?? ''
  const generateLabel = copy ? 'AIで作り直す' : 'AIで仕上げる'

  return (
    <div className={cn('space-y-3', className)}>
      <div className="grid grid-cols-3 gap-2">
        {VARIANTS.map((item) => (
          <Button
            key={item.value}
            variant={item.value === variant ? 'default' : 'outline'}
            size="sm"
            onClick={() => setVariant(item.value)}
            disabled={isGenerating}
          >
            {item.label}
          </Button>
        ))}
      </div>
      <p className="text-muted-foreground text-xs">{activeHint}</p>

      <div className="relative overflow-hidden rounded-xl border bg-[#0f1115]">
        <canvas ref={canvasRef} className="block h-auto w-full" />
        {(isRendering || isGenerating) && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/60">
            <Loader2 className="size-8 animate-spin text-white" />
            {isGenerating && <p className="text-xs text-white">AI が旅を読み込んでいます…</p>}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Button variant="outline" onClick={() => void handleGenerate()} disabled={!AI_ENABLED || isGenerating}>
          <Sparkles className="size-4" />
          {generateLabel}
        </Button>
        <Button onClick={handleDownload} disabled={isRendering || isGenerating}>
          <Download className="size-4" />
          保存
        </Button>
      </div>
    </div>
  )
}
